import React, { useState, useRef, useEffect } from 'react';
import { nlSearch, cleanApiBaseUrl } from '../lib/api';
import toast from 'react-hot-toast';
import { Send, Database, Code, AlertCircle, RefreshCw, Play } from 'lucide-react';

const EXAMPLES = [
  "Show all building permits issued this year",
  "Which contractors have the most permits?",
  "Show residential permits over $100,000",
  "Find resolutions from the last 6 months",
  "Which department has the most published records?",
  "How many ordinances are in draft status?",
];

function formatValue(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    return new Date(val).toLocaleDateString();
  }
  if (typeof val === 'string' && val.length > 80) return val.slice(0, 80) + '…';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function formatHeader(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function ResultTable({ results }) {
  if (!results || results.length === 0) {
    return <div className="text-center py-4 text-sm text-gray-400">No results found for this query.</div>;
  }
  const columns = Object.keys(results[0]);
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mt-3">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-50 bg-gray-50/50">
              {columns.map(col => (
                <th key={col} className="px-4 py-3 text-left text-xs font-medium text-gray-400 whitespace-nowrap">
                  {formatHeader(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.map((row, i) => (
              <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                {columns.map(col => (
                  <td key={col} className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap max-w-xs truncate">
                    {formatValue(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function NLSearchPage() {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [executingIdx, setExecutingIdx] = useState(null);
  const [examples, setExamples] = useState(EXAMPLES);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, generating]);

  useEffect(() => {
    inputRef.current?.focus();

    fetch(`${cleanApiBaseUrl()}/api/config`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data.smartquery_examples) && data.smartquery_examples.length) setExamples(data.smartquery_examples);
      })
      .catch(() => {});
  }, []);

  const handleGenerate = async (q) => {
    const text = q || question.trim();
    if (!text) return;
    setQuestion('');
    setGenerating(true);

    // Add user message
    setMessages(m => [...m, { role: 'user', text }]);

    try {
      const { data } = await nlSearch.generate(text);
      setMessages(m => [...m, {
        role: 'assistant',
        type: 'sql',
        sql: data.sql,
        database: data.database || 'records',
        duration_ms: data.duration_ms,
        results: null,
        executed: false,
        executing: false,
        error: null
      }]);
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to generate query';
      setMessages(m => [...m, {
        role: 'assistant',
        type: 'error',
        text: msg,
        detail: e.response?.data?.detail,
        failedSQL: e.response?.data?.sql
      }]);
      toast.error(msg);
    } finally {
      setGenerating(false);
      inputRef.current?.focus();
    }
  };

  const handleExecute = async (msgIdx) => {
    const msg = messages[msgIdx];
    if (!msg || msg.type !== 'sql' || msg.executed) return;

    setExecutingIdx(msgIdx);
    setMessages(m => m.map((msg, i) => i === msgIdx ? { ...msg, executing: true } : msg));

    try {
      const { data } = await nlSearch.execute(msg.sql, msg.database || 'records');
      setMessages(m => m.map((msg, i) => i === msgIdx ? {
        ...msg,
        executed: true,
        executing: false,
        results: data.results,
        resultCount: data.count,
        execDuration: data.duration_ms
      } : msg));
    } catch (e) {
      const errMsg = e.response?.data?.error || 'Query execution failed';
      setMessages(m => m.map((msg, i) => i === msgIdx ? {
        ...msg,
        executing: false,
        error: errMsg,
        errorDetail: e.response?.data?.detail
      } : msg));
      toast.error(errMsg);
    } finally {
      setExecutingIdx(null);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-screen">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 bg-white flex items-center gap-3 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
          <Database size={14} className="text-white" />
        </div>
        <div>
          <h1 className="text-sm font-semibold text-gray-900">Smart Query</h1>
          <p className="text-xs text-gray-400">Ask questions in plain English — review the generated SQL, then run it</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {/* Examples — shown when no messages */}
        {messages.length === 0 && !generating && (
          <div className="max-w-2xl mx-auto pt-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center mx-auto mb-3">
                <Database size={20} className="text-teal-500" />
              </div>
              <h2 className="text-base font-medium text-gray-800">Search records with natural language</h2>
              <p className="text-sm text-gray-400 mt-1">Your question is converted to SQL — you review it before it runs</p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {examples.map((q, i) => (
                <button key={i} onClick={() => { setQuestion(q); handleGenerate(q); }} disabled={generating}
                  className="text-left px-4 py-3 bg-white rounded-xl border border-gray-100 text-sm text-gray-600 hover:border-teal-200 hover:text-teal-700 hover:bg-teal-50/30 transition-all disabled:opacity-40">
                  "{q}"
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat messages */}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} max-w-4xl mx-auto w-full`}>
            {msg.role === 'user' ? (
              <div className="bg-teal-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-lg text-sm">
                {msg.text}
              </div>
            ) : msg.type === 'error' ? (
              <div className="flex-1 max-w-3xl">
                <div className="bg-red-50 border border-red-100 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex items-center gap-2 text-red-700 text-sm font-medium">
                    <AlertCircle size={14} />
                    {msg.text}
                  </div>
                  {msg.detail && <p className="text-xs text-red-500 mt-1">{msg.detail}</p>}
                  {msg.failedSQL && (
                    <pre className="mt-2 bg-red-900 text-red-200 rounded-lg p-3 text-xs font-mono overflow-x-auto">{msg.failedSQL}</pre>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 max-w-3xl">
                {/* SQL block */}
                <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm overflow-hidden">
                  <div className="bg-gray-900 rounded-t-xl p-4 overflow-x-auto">
                    <div className="flex items-center gap-2 mb-2">
                      <Code size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-400 font-medium">
                        {msg.executed ? 'Executed SQL' : 'Generated SQL'}
                      </span>
                      {msg.database && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          msg.database === 'cityworks'
                            ? 'bg-purple-900/40 text-purple-300 border border-purple-700'
                            : 'bg-teal-900/40 text-teal-300 border border-teal-700'
                        }`}>
                          {msg.database === 'cityworks' ? 'City Works DB' : 'CogniVault Records'}
                        </span>
                      )}
                      {msg.duration_ms && (
                        <span className="text-xs text-gray-500 ml-auto">{(msg.duration_ms / 1000).toFixed(1)}s</span>
                      )}
                    </div>
                    <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap">{msg.sql}</pre>
                  </div>

                  {/* Run button — only if not executed yet */}
                  {!msg.executed && !msg.executing && !msg.error && (
                    <div className="px-4 py-3 flex items-center gap-3">
                      <button onClick={() => handleExecute(i)} disabled={executingIdx !== null}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all disabled:opacity-40">
                        <Play size={14} />
                        Run Query
                      </button>
                    </div>
                  )}

                  {/* Executing spinner */}
                  {msg.executing && (
                    <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-400">
                      <RefreshCw size={14} className="animate-spin text-teal-400" />
                      Running query...
                    </div>
                  )}

                  {/* Execution error */}
                  {msg.error && (
                    <div className="px-4 py-3">
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle size={14} />
                        {msg.error}
                      </div>
                      {msg.errorDetail && <p className="text-xs text-red-400 mt-1">{msg.errorDetail}</p>}
                    </div>
                  )}

                  {/* Results metadata */}
                  {msg.executed && (
                    <div className="px-4 py-2 flex items-center gap-4 text-xs text-gray-400 border-t border-gray-50">
                      <span>{msg.resultCount} result{msg.resultCount !== 1 ? 's' : ''}</span>
                      {msg.execDuration && <span>{(msg.execDuration / 1000).toFixed(1)}s</span>}
                    </div>
                  )}
                </div>

                {/* Results table — below the message */}
                {msg.executed && msg.results && (
                  <ResultTable results={msg.results} />
                )}
              </div>
            )}
          </div>
        ))}

        {/* Generating spinner */}
        {generating && (
          <div className="flex justify-start max-w-4xl mx-auto w-full">
            <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
              <RefreshCw size={14} className="animate-spin text-teal-400" />
              <span className="text-sm text-gray-400">Generating SQL query...</span>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-gray-100 bg-white flex-shrink-0">
        <div className="max-w-4xl mx-auto flex gap-3">
          <input ref={inputRef}
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate(); } }}
            disabled={generating || executingIdx !== null}
            placeholder="Ask a question about city records..."
            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 disabled:bg-gray-50 disabled:text-gray-400" />
          <button onClick={() => handleGenerate()} disabled={generating || executingIdx !== null || !question.trim()}
            className="w-11 h-11 bg-gradient-to-br from-teal-600 to-cyan-600 text-white rounded-xl flex items-center justify-center hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
            <Send size={15} />
          </button>
        </div>
        <div className="max-w-4xl mx-auto mt-1.5">
          <p className="text-xs text-gray-300 text-center">Questions generate SQL for you to review · Click "Run Query" to execute</p>
        </div>
      </div>
    </div>
  );
}
